import React, { useState } from 'react';
import { Plus, Search, Table, Edit2, Copy, Trash2, MoreVertical, Star, Users } from 'lucide-react';
import {
  useListViews,
  useDeleteListView,
  useCloneListView,
  type ListView
} from '../../../services/queries/listViewQueries';
import { USE_GOBODO_API } from '../../../utils/gobodoUtils';
import { DevModeBanner } from '../../common/DevModeBanner';
import { ListViewBuilder } from './ListViewBuilder';
import type { CustomField } from '../../../services/queries/fieldQueries';
import { useToast } from '../../../contexts/ToastContext';
import {
  StandardButton,
  StandardSearchBar,
  StandardLoadingState,
  StandardErrorState,
  StandardEmptyState
} from '../../standard';

interface ListViewsHomeProps {
  objectName: string;
  objectLabel: string;
  allFields: CustomField[];
}

export const ListViewsHome: React.FC<ListViewsHomeProps> = ({
  objectName,
  objectLabel,
  allFields
}) => {
  const { success, error: showError } = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [viewBuilderOpen, setViewBuilderOpen] = useState<{ view?: ListView; objectName: string } | null>(null);
  const [viewMenuOpen, setViewMenuOpen] = useState<string | null>(null);

  const { data: views, isLoading, error } = useListViews(objectName);
  const deleteViewMutation = useDeleteListView();
  const cloneViewMutation = useCloneListView();

  const filteredViews = views?.filter(view =>
    view.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    view.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalViews = views?.length || 0;
  const sharedViews = views?.filter(v => v.isShared).length || 0;
  const defaultView = views?.find(v => v.isDefault);

  const handleDelete = async (viewId: string, viewName: string) => {
    if (!window.confirm(`Are you sure you want to delete "${viewName}"?`)) return;

    try {
      await deleteViewMutation.mutateAsync({ viewId, objectName });
      success('List view deleted successfully!', `${viewName} has been permanently removed`);
      setViewMenuOpen(null);
    } catch (error: any) {
      showError('Failed to delete list view', error.message || 'Please try again');
    }
  };

  const handleClone = async (viewId: string) => {
    try {
      const cloned = await cloneViewMutation.mutateAsync(viewId);
      setViewBuilderOpen({ view: cloned, objectName });
      setViewMenuOpen(null);
    } catch (error) {
      console.error('Failed to clone list view:', error);
    }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">List Views</h2>
            <p className="text-gray-600 dark:text-gray-300 mt-1">
              Configure how {objectLabel} records appear in list views
            </p>
          </div>
          <StandardButton
            variant="primary"
            leftIcon={<Plus className="w-4 h-4" />}
            onClick={() => setViewBuilderOpen({ objectName })}
          >
            New List View
          </StandardButton>
        </div>

        {/* Development Mode Banner */}
        <DevModeBanner
          dataSource={USE_GOBODO_API ? 'Gobodo API' : 'mockDataManager.ts'}
          itemCount={views ? `${views.length} list views` : 'list views'}
          customMessage={USE_GOBODO_API
            ? `Gobodo: ${views ? `Showing ${views.length} list views` : 'Loading'} from Gobodo API. Changes persist to backend.`
            : `Development Mode: ${views ? `Showing ${views.length} list views` : 'Using mock data'} (in-memory).`}
          dismissible={true}
        />

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white">
                  {totalViews}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-300">Total Views</div>
              </div>
              <div className="p-3 bg-purple-100 dark:bg-purple-900/20 rounded-lg">
                <Table className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white">
                  {sharedViews}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-300">Shared Views</div>
              </div>
              <div className="p-3 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                <Users className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm text-gray-600 dark:text-gray-300 mb-1">Default View</div>
                <div className="font-semibold text-gray-900 dark:text-white truncate">
                  {defaultView?.name || 'None'}
                </div>
              </div>
              <div className="p-3 bg-yellow-100 dark:bg-yellow-900/20 rounded-lg">
                <Star className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
              </div>
            </div>
          </div>
        </div>

        {/* Search */}
        <StandardSearchBar
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Search list views..."
        />
      </div>

      {/* Loading State */}
      {isLoading && (
        <StandardLoadingState message="Loading list views..." />
      )}

      {/* Error State */}
      {error && (
        <StandardErrorState
          title="Error loading list views"
          message="Please try again."
        />
      )}

      {/* Views Grid */}
      {!isLoading && !error && (
        <>
          {filteredViews && filteredViews.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredViews.map((view) => (
                <ViewCard
                  key={view.id}
                  view={view}
                  onEdit={() => setViewBuilderOpen({ view, objectName })}
                  onClone={() => handleClone(view.id)}
                  onDelete={() => handleDelete(view.id, view.name)}
                  menuOpen={viewMenuOpen === view.id}
                  onMenuToggle={() => setViewMenuOpen(viewMenuOpen === view.id ? null : view.id)}
                />
              ))}
            </div>
          ) : (
            <StandardEmptyState
              icon={<Table className="w-16 h-16" />}
              title={searchQuery ? 'No list views found' : 'No list views yet'}
              message={searchQuery
                ? 'Try adjusting your search criteria'
                : 'Create your first list view to get started'
              }
              actionLabel={searchQuery ? 'Clear search' : 'Create List View'}
              onAction={searchQuery
                ? () => setSearchQuery('')
                : () => setViewBuilderOpen({ objectName })
              }
            />
          )}
        </>
      )}

      {/* List View Builder Modal */}
      {viewBuilderOpen && (
        <ListViewBuilder
          view={viewBuilderOpen.view}
          objectName={viewBuilderOpen.objectName}
          objectLabel={objectLabel}
          allFields={allFields}
          onClose={() => setViewBuilderOpen(null)}
        />
      )}
    </div>
  );
};

// ============================================
// VIEW CARD COMPONENT
// ============================================

interface ViewCardProps {
  view: ListView;
  onEdit: () => void;
  onClone: () => void;
  onDelete: () => void;
  menuOpen: boolean;
  onMenuToggle: () => void;
}

const ViewCard: React.FC<ViewCardProps> = ({
  view,
  onEdit,
  onClone,
  onDelete,
  menuOpen,
  onMenuToggle
}) => {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-lg transition-shadow">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-start justify-between mb-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-semibold text-gray-900 dark:text-white truncate">
                {view.name}
              </h3>
              {view.isDefault && (
                <Star className="w-4 h-4 text-yellow-600 dark:text-yellow-400 fill-current flex-shrink-0" />
              )}
            </div>
            {view.description && (
              <p className="text-sm text-gray-600 dark:text-gray-300 line-clamp-2">
                {view.description}
              </p>
            )}
          </div>

          <div className="relative ml-2">
            <button
              onClick={onMenuToggle}
              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
            >
              <MoreVertical className="w-4 h-4 text-gray-500 dark:text-gray-400" />
            </button>

            {menuOpen && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={onMenuToggle}
                />
                <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-20">
                  <div className="py-1">
                    <button
                      onClick={onEdit}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                    >
                      <Edit2 className="w-4 h-4" />
                      Edit View
                    </button>
                    <button
                      onClick={onClone}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                    >
                      <Copy className="w-4 h-4" />
                      Clone View
                    </button>
                    {!view.isDefault && (
                      <>
                        <div className="border-t border-gray-200 dark:border-gray-700 my-1" />
                        <button
                          onClick={onDelete}
                          className="w-full px-4 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete View
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {view.isShared && (
            <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 text-xs rounded font-medium">
              Shared
            </span>
          )}
          {!view.isShared && (
            <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-400 text-xs rounded font-medium">
              Private
            </span>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="p-4">
        <div className="space-y-3 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-gray-600 dark:text-gray-400">Columns:</span>
            <span className="font-semibold text-gray-900 dark:text-white">{view.columns.length}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-600 dark:text-gray-400">Filters:</span>
            <span className="font-semibold text-gray-900 dark:text-white">{view.filters.length}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-600 dark:text-gray-400">Sort By:</span>
            <span className="font-semibold text-gray-900 dark:text-white truncate ml-2">
              {view.sort.length > 0 ? view.sort[0].fieldLabel : 'None'}
            </span>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="px-4 py-3 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
          <span>By {view.createdBy}</span>
          <span>{new Date(view.lastModified).toLocaleDateString()}</span>
        </div>
      </div>
    </div>
  );
};

